import { useEffect, useRef } from 'react'
import type { TaskDocument } from '@/lib/db/rxdb/hooks/entities/tasks'
import { getRxDB } from '@/lib/db/rxdb'
import { generateInitialRank, generateNextRank } from '@/lib/utils/lexorank'

/**
 * Lazy migration for tasks with null sortOrder.
 *
 * On first render where any task lacks a sortOrder, walks the workspace's
 * tasks in createdOn ASC and assigns LexoRanks via generateInitialRank /
 * generateNextRank. This brings pre-fix accounts (where tasks were created
 * before either client started stamping sortOrder on insert) into the same
 * canonical order the backend's BackfillWorkItemSortOrder migration uses.
 *
 * createdOn ASC is critical: the input array comes from the upstream useTasks
 * query which has no sort guarantee (RxDB primary-key order), so walking it
 * directly would freeze whatever arbitrary order RxDB happened to emit into
 * permanent LexoRanks. The display order then diverges from the backend's
 * canonical createdOn-ASC order any time a client-generated rank wins LWW
 * against the backend's rank.
 */
export function useSortOrderMigration(tasks: Array<TaskDocument>) {
  const migrated = useRef(false)

  useEffect(() => {
    if (migrated.current || tasks.length === 0) return

    const needsMigration = tasks.filter((t) => !t.sortOrder)
    if (needsMigration.length === 0) {
      migrated.current = true
      return
    }

    migrated.current = true

    const migrate = async () => {
      const db = getRxDB()
      let previousRank: string | null = null

      const walkOrder = [...tasks].sort(
        (a, b) => (a.createdOn ?? 0) - (b.createdOn ?? 0),
      )

      for (const task of walkOrder) {
        if (task.sortOrder) {
          previousRank = task.sortOrder
          continue
        }

        const nextRank: string =
          previousRank === null
            ? generateInitialRank()
            : generateNextRank(previousRank)
        previousRank = nextRank

        try {
          const doc = await db.tasks.findOne(task.id).exec()
          if (doc && !doc.sortOrder) {
            await doc.update({
              $set: {
                sortOrder: nextRank,
                updatedOn: Date.now(),
              },
            })
          }
        } catch {
          // Skip failed individual task migrations
        }
      }
    }

    migrate()
  }, [tasks])
}
